// src/services/LiveService.ts
import axios from "axios";
import { baseURL } from "../Constants";

// Define the interfaces for our data structures
export interface Player {
  steamId: string;
  name: string;
  team: string;
  kills: number;
  deaths: number;
  assists: number;
  money: number;
  isAlive: boolean;
  health: number;
  armor: number;
  hasHelmet: boolean;
  weapons: string[];
}

export interface Team {
  teamId: number;
  teamName: string;
  logoUrl: string;
  score: number;
  timeoutsRemaining: number;
}

export interface LiveMatch {
  matchId: string;
  mapName: string;
  ctTeam: Team;
  tTeam: Team;
  currentRound: number;
  gamePhase: string;
  isLive: boolean;
  isStreaming: boolean;
  streamerName: string;
  players: Player[];
  timestamp: string;
}

const url = `${baseURL}/Live`;

// Get all live matches
export const getLiveMatches = async (): Promise<LiveMatch[]> => {
  try {
    const response = await axios.get(`${url}/matches`);
    return response.data;
  } catch (error) {
    console.error("Error fetching live matches:", error);
    throw error;
  }
};

// Get specific match details
export const getLiveMatch = async (matchId: string): Promise<LiveMatch> => {
  try {
    const response = await axios.get(`${url}/matches/${matchId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching live match ${matchId}:`, error);
    throw error;
  }
};

// Get live matches for a specific team
export const getLiveMatchesByTeam = async (teamId: number): Promise<LiveMatch[]> => {
  try {
    const response = await axios.get(`${url}/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching live matches for team ${teamId}:`, error);
    throw error;
  }
};

// Helper functions that components might use
export const formatMapName = (mapName: string): string => {
  if (!mapName) return '';
  return mapName.replace('de_', '').charAt(0).toUpperCase() + mapName.replace('de_', '').slice(1);
};

export const formatPhase = (phase: string): string => {
  if (!phase) return '';
  
  switch (phase) {
    case 'freezetime': return 'Freeze Time';
    case 'live': return 'Round Live';
    case 'over': return 'Round Over';
    case 'bomb': return 'Bomb Planted';
    case 'defuse': return 'Defusing';
    case 'timeout_ct': return 'CT Timeout';
    case 'timeout_t': return 'T Timeout';
    case 'warmup': return 'Warmup';
    default: return phase.charAt(0).toUpperCase() + phase.slice(1);
  }
};

export const calculateKD = (kills: number, deaths: number): string => {
  if (deaths === 0) return kills.toFixed(2);
  return (kills / deaths).toFixed(2);
};

export const formatWeaponName = (weaponCode: string): string => {
  const weapons: { [key: string]: string } = {
    'deagle': 'Deagle',
    'elite': 'Dual',
    'fiveseven': '5-7',
    'glock': 'Glock',
    'ak47': 'AK-47',
    'aug': 'AUG',
    'awp': 'AWP',
    'famas': 'FAMAS',
    'g3sg1': 'G3SG1',
    'm249': 'M249',
    'm4a1': 'M4A4',
    'm4a1_silencer': 'M4A1-S',
    'mac10': 'MAC-10',
    'p90': 'P90',
    'mp5sd': 'MP5',
    'ump45': 'UMP',
    'xm1014': 'XM1014',
    'bizon': 'Bizon',
    'mag7': 'MAG-7',
    'negev': 'Negev',
    'sawedoff': 'Sawed-Off',
    'tec9': 'Tec-9',
    'hkp2000': 'P2000',
    'mp7': 'MP7',
    'mp9': 'MP9',
    'nova': 'Nova',
    'p250': 'P250',
    'scar20': 'SCAR-20',
    'sg556': 'SG 553',
    'ssg08': 'Scout',
    'usp_silencer': 'USP-S',
    'cz75a': 'CZ75',
    'revolver': 'R8',
    'knife': 'Knife',
    'hegrenade': 'HE',
    'flashbang': 'Flash',
    'smokegrenade': 'Smoke',
    'molotov': 'Molotov',
    'incgrenade': 'Incendiary',
    'decoy': 'Decoy',
    'taser': 'Zeus',
    'c4': 'C4'
  };

  return weapons[weaponCode] || weaponCode;
};